import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Search, MapPin, Phone, Globe, Star, StarOff, 
  ChevronRight, ThumbsUp, ThumbsDown, Minus, Navigation
} from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';
import AddPlaceDialog from './AddPlaceDialog';
import PlaceDetailSheet from './PlaceDetailSheet';
import { format, parseISO } from 'date-fns';
import { useDemo } from '@/contexts/DemoContext';

const CATEGORY_COLORS = {
  restaurant: { bg: 'bg-orange-100', text: 'text-orange-700' },
  mechanic: { bg: 'bg-blue-100', text: 'text-blue-700' },
  doctor: { bg: 'bg-green-100', text: 'text-green-700' },
  store: { bg: 'bg-purple-100', text: 'text-purple-700' },
  service: { bg: 'bg-amber-100', text: 'text-amber-700' },
  office: { bg: 'bg-slate-100', text: 'text-slate-700' },
  other: { bg: 'bg-pink-100', text: 'text-pink-700' },
};

const RATING_ICONS = {
  excellent: { icon: ThumbsUp, color: 'text-green-600', bg: 'bg-green-100' },
  good: { icon: ThumbsUp, color: 'text-blue-600', bg: 'bg-blue-100' },
  neutral: { icon: Minus, color: 'text-slate-600', bg: 'bg-slate-100' },
  bad: { icon: ThumbsDown, color: 'text-red-600', bg: 'bg-red-100' },
};

const GO_BACK_COLORS = {
  yes: 'bg-green-100 text-green-700',
  no: 'bg-red-100 text-red-700',
  maybe: 'bg-amber-100 text-amber-700',
};

const PlacesTab = () => {
  const { isDemoMode, getPlaces } = useDemo();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    fetchPlaces();
  }, [isDemoMode]);

  const fetchPlaces = async () => {
    try {
      setLoading(true);
      
      if (isDemoMode) {
        const demoPlaces = getPlaces() || [];
        setPlaces(demoPlaces);
        setLoading(false);
        return;
      }
      
      const response = await API.get('/places');
      setPlaces(response.data || []);
    } catch (error) {
      console.error('Failed to fetch places:', error);
      if (!isDemoMode) {
        toast.error('Failed to load places');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceAdded = () => {
    fetchPlaces();
    setShowAddDialog(false);
    toast.success('Place added successfully');
  };

  const handlePlaceUpdated = () => {
    fetchPlaces();
    setSelectedPlace(null);
    toast.success('Place updated');
  };

  const handleDeletePlace = async (placeId) => {
    try {
      await API.delete(`/places/${placeId}`);
      fetchPlaces();
      setSelectedPlace(null);
      toast.success('Place deleted');
    } catch (error) {
      toast.error('Failed to delete place');
    }
  };

  const handleToggleFavorite = async (place) => {
    try {
      await API.put(`/places/${place.id}`, {
        is_favorite: !place.is_favorite
      });
      fetchPlaces();
    } catch (error) {
      toast.error('Failed to update place');
    }
  };

  const handleNavigate = (place) => {
    if (place.address) {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.address)}`;
      window.open(url, '_blank');
    }
  };

  const handleCall = (place) => {
    if (place.phone) {
      window.location.href = `tel:${place.phone}`;
    }
  };

  const filteredPlaces = places.filter(place => {
    const matchesSearch = place.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (place.address && place.address.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = filterCategory === 'all' || place.category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  const sortedPlaces = [...filteredPlaces].sort((a, b) => {
    if (a.is_favorite && !b.is_favorite) return -1;
    if (!a.is_favorite && b.is_favorite) return 1;
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="places-tab">
      {/* Action Button */}
      <div className="flex justify-end">
        <Button 
          onClick={() => setShowAddDialog(true)}
          className="bg-pink-500 hover:bg-pink-600"
          data-testid="add-place-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Place
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search places..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-white/50"
            data-testid="search-places"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 bg-white/50 text-sm"
          data-testid="filter-category"
        >
          <option value="all">All Categories</option>
          <option value="restaurant">Restaurant</option>
          <option value="mechanic">Mechanic</option>
          <option value="doctor">Doctor</option>
          <option value="store">Store</option>
          <option value="service">Service</option>
          <option value="office">Office</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Places List */}
      {sortedPlaces.length === 0 ? (
        <div className="text-center py-12">
          <MapPin className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium mb-2">Save Your Favorite Places</p>
          <p className="text-sm text-slate-400 mb-4">
            Keep track of restaurants, doctors, mechanics, and more with your personal notes
          </p>
          <Button 
            onClick={() => setShowAddDialog(true)}
            variant="outline"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Place
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {sortedPlaces.map((place) => {
            const categoryColor = CATEGORY_COLORS[place.category] || CATEGORY_COLORS.other;
            const rating = place.experience_rating ? RATING_ICONS[place.experience_rating] : null;
            
            return (
              <Card 
                key={place.id}
                className="cursor-pointer hover:shadow-md transition-shadow border-pink-100/50"
                onClick={() => setSelectedPlace(place)}
                data-testid={`place-card-${place.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${categoryColor.bg}`}>
                      <MapPin className={`h-6 w-6 ${categoryColor.text}`} />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-800 truncate">{place.name}</h4>
                        {place.is_favorite && (
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500 flex-shrink-0" />
                        )}
                      </div>
                      
                      {place.address && (
                        <p className="text-sm text-slate-500 truncate mt-0.5">
                          {place.address}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge className={`${categoryColor.bg} ${categoryColor.text} text-xs`}>
                          {place.category}
                        </Badge>
                        
                        {place.would_go_back && (
                          <Badge className={`${GO_BACK_COLORS[place.would_go_back]} text-xs`}>
                            {place.would_go_back === 'yes' ? 'Would Return' : 
                             place.would_go_back === 'no' ? 'Wouldn\'t Return' : 'Maybe Return'}
                          </Badge>
                        )}
                        
                        {rating && (
                          <span className={`flex items-center gap-1 text-xs ${rating.color}`}>
                            <rating.icon className="h-3 w-3" />
                            {place.experience_rating}
                          </span>
                        )}
                        
                        {place.last_visit_date && (
                          <span className="text-xs text-slate-400">
                            Last: {format(parseISO(place.last_visit_date), 'MMM d, yyyy')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="flex items-center gap-1">
                      {place.address && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigate(place);
                          }}
                          className="p-2 text-blue-600"
                          title="Navigate"
                        >
                          <Navigation className="h-4 w-4" />
                        </Button>
                      )}
                      {place.phone && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCall(place);
                          }}
                          className="p-2 text-green-600"
                          title="Call"
                        >
                          <Phone className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(place);
                        }}
                        className="p-2"
                      >
                        {place.is_favorite ? (
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                        ) : (
                          <StarOff className="h-4 w-4 text-slate-400" />
                        )}
                      </Button>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <AddPlaceDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onPlaceAdded={handlePlaceAdded}
      />

      <PlaceDetailSheet
        place={selectedPlace}
        open={!!selectedPlace}
        onOpenChange={(open) => !open && setSelectedPlace(null)}
        onPlaceUpdated={handlePlaceUpdated}
        onPlaceDeleted={() => handleDeletePlace(selectedPlace?.id)}
      />
    </div>
  );
};

export default PlacesTab;
